import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/hooks/useAuth';
import { TOOLKITS, DEFAULT_TOOLKIT_IDS, type ToolkitModule } from '@/lib/toolkits';
import type { Organization, ToolkitId } from '@/types/models';

/**
 * Returns the list of ToolkitModule entries that the current organisation
 * is entitled to.
 *
 * - admin_global (without an assumed org): returns all toolkits.
 * - Org user (or admin acting as org): reads organizations/{orgId}.toolkits.
 *   Falls back to DEFAULT_TOOLKIT_IDS when the field is absent.
 */
export function useEntitledToolkits(): ToolkitModule[] {
  const { isGlobalAdmin, effectiveOrgId, claims } = useAuth();
  const [toolkitIds, setToolkitIds] = useState<ToolkitId[] | null>(null);

  useEffect(() => {
    // Global admin not acting as any org → show everything
    if (isGlobalAdmin && !claims.assumedOrgId) {
      setToolkitIds(TOOLKITS.map(tk => tk.id));
      return;
    }

    if (!effectiveOrgId) {
      setToolkitIds(DEFAULT_TOOLKIT_IDS);
      return;
    }

    const unsub = onSnapshot(
      doc(db, 'organizations', effectiveOrgId),
      snap => {
        if (!snap.exists()) {
          setToolkitIds(DEFAULT_TOOLKIT_IDS);
          return;
        }
        const org = snap.data() as Partial<Organization>;
        setToolkitIds(org.toolkits ?? DEFAULT_TOOLKIT_IDS);
      },
      () => {
        // On permission error or network failure, fall back to default
        setToolkitIds(DEFAULT_TOOLKIT_IDS);
      },
    );
    return unsub;
  }, [isGlobalAdmin, effectiveOrgId, claims.assumedOrgId]);

  if (toolkitIds === null) return [];

  return TOOLKITS.filter(tk => toolkitIds.includes(tk.id));
}
